import React from "react";
import { Anchor, MessageCircle, MapPin, Phone, ArrowRight, TreePalm, Waves, Sun } from "lucide-react";
import { HERO_FEATURES, SITE, buildWhatsAppLink } from "../mock/mock";

const iconMap = {
  anchor: Anchor,
  palm: TreePalm,
  mask: Waves,
  sun: Sun,
};

const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="accueil"
      className="relative min-h-[88vh] overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1778706141531-8c84ceb23470?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxHdWFkZWxvdXBlJTIwdHVycXVvaXNlfGVufDB8fHx8MTc4Mzk3MTAxOXww&ixlib=rb-4.1.0&q=85"
          alt="Lagon turquoise de Guadeloupe"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B2545]/60 via-[#0B2545]/20 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B2545]/40 via-transparent to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 lg:px-6 pt-6 md:pt-10 pb-16">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          {/* Left content */}
          <div className="lg:col-span-8">
            <h1 className="text-white font-black leading-[0.9] drop-shadow-2xl">
              <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-8xl tracking-tight">
                GWADAPLAISANCE
              </span>
              <span className="block mt-3 md:mt-4 text-2xl sm:text-3xl md:text-4xl lg:text-6xl text-[#FFC93C] italic" style={{ fontFamily: "'Caveat Brush', cursive" }}>
                EXCURSIONS EN MER
              </span>
            </h1>

            <div className="mt-5 md:mt-6 space-y-3">
              <p className="text-white text-base sm:text-lg md:text-2xl font-semibold tracking-wide drop-shadow">
                GRAND CUL DE SAC MARIN – GUADELOUPE
              </p>
              <p className="text-white/95 text-lg sm:text-xl md:text-3xl italic border-b-4 border-[#FFC93C] inline-block pb-1" style={{ fontFamily: "'Caveat', cursive" }}>
                Vivez la Guadeloupe autrement !
              </p>
            </div>

            {/* Feature icons */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
              {HERO_FEATURES.map((f, i) => {
                const Icon = iconMap[f.icon] || Anchor;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-white/95 flex items-center justify-center shadow-lg">
                      <Icon className="h-6 w-6 text-[#0B2545]" />
                    </div>
                    <div className="text-white text-xs font-bold leading-tight drop-shadow">
                      <div>{f.title}</div>
                      <div className="font-normal text-white/85">{f.subtitle}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CTAs */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => scrollTo("excursions")}
                className="group inline-flex items-center justify-between gap-3 bg-[#FFC93C] hover:bg-[#FFB800] text-[#0B2545] font-black px-6 py-4 rounded-xl shadow-xl transition-all duration-200 hover:-translate-y-0.5"
              >
                <span className="tracking-wide">DÉCOUVRIR NOS EXCURSIONS</span>
                <span className="h-8 w-8 rounded-full bg-[#0B2545] flex items-center justify-center group-hover:translate-x-1 transition-transform">
                  <ArrowRight className="h-4 w-4 text-[#FFC93C]" />
                </span>
              </button>

              <a
                href={buildWhatsAppLink("Bonjour, je souhaite réserver une excursion en mer.")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1EBE5D] text-white font-black px-6 py-4 rounded-xl shadow-xl transition-all duration-200 hover:-translate-y-0.5"
              >
                <MessageCircle className="h-6 w-6" fill="white" strokeWidth={0} />
                <span className="tracking-wide">RÉSERVER SUR WHATSAPP</span>
              </a>
            </div>
          </div>

          {/* Right info card */}
          <div className="lg:col-span-4">
            <div className="bg-[#0B2545]/95 backdrop-blur-md rounded-2xl p-6 shadow-2xl border border-white/10">
              <div className="flex items-start gap-3 pb-5 border-b border-white/15">
                <div className="h-10 w-10 rounded-full bg-[#FFC93C] flex items-center justify-center shrink-0">
                  <MapPin className="h-5 w-5 text-[#0B2545]" />
                </div>
                <div>
                  <div className="text-[#FFC93C] text-xs font-bold tracking-widest">DÉPART</div>
                  <div className="text-white font-bold text-lg">{SITE.location}</div>
                  <div className="text-white/70 text-sm">{SITE.locationDetail}</div>
                </div>
              </div>

              <div className="flex items-start gap-3 pt-5">
                <div className="h-10 w-10 rounded-full bg-[#25D366] flex items-center justify-center shrink-0">
                  <Phone className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-[#FFC93C] text-xs font-bold tracking-widest">
                    INFOS & RÉSERVATIONS
                  </div>
                  <a
                    href={`tel:+590${SITE.phoneRaw.substring(1)}`}
                    className="text-white font-black text-2xl tracking-wider hover:text-[#FFC93C] transition-colors"
                  >
                    {SITE.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wave bottom */}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
        <svg viewBox="0 0 1440 80" className="w-full h-16 md:h-20" preserveAspectRatio="none">
          <path
            d="M0,40 C240,80 480,0 720,32 C960,64 1200,16 1440,40 L1440,80 L0,80 Z"
            fill="#f8fafc"
          />
        </svg>
      </div>
    </section>
  );
};

export default Hero;
